import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Store } from '@ngrx/store';

import { BehaviorSubject, catchError, map, Observable, of, shareReplay } from 'rxjs';

import {
  AuthTokenService,
  DocumentStatus,
  FileDownloadService,
  TicketAttachmentStatus,
  FileExtension,
  getFileExtensionFromPath,
  getSelectedCustomerIdent,
  IActionState,
  IEnvironment,
  IResidentS3File,
} from '@resident-nx/shared';

@Injectable()
export class FileDownloadWebService extends FileDownloadService {
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  private store = inject(Store);
  private customerIdent = this.store.selectSignal(getSelectedCustomerIdent);

  public documentDownloadActionState$: BehaviorSubject<IActionState>;

  // Keyed by `${contentId}-${titleImageId}` so that several cards showing the same content share a
  // single request, while a replaced title image (new id) bypasses the cached blob url.
  readonly #titleImageCache = new Map<string, Observable<string>>();

  constructor(
    override authTokenService: AuthTokenService,
    @Inject('ENVIRONMENT') environment: IEnvironment
  ) {
    super(authTokenService, environment);
  }

  public getDocument(documentId: string): Observable<{
    preview: SafeResourceUrl;
    download: string;
    isRequested: boolean;
    isAlreadyRequested: boolean;
    isUnavailable: boolean;
    extension: FileExtension;
  }> {
    const request = this.createRequestForDocument(documentId, this.customerIdent());
    return this.http
      .get(request.url, {
        headers: request.headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(response => {
          const documentStatus = response.headers.get('X-Document-Status');
          const contentType = response.headers.get('content-type');
          if (
            documentStatus === DocumentStatus.DOCUMENT_REQUESTED ||
            documentStatus === DocumentStatus.DOCUMENT_ALREADY_REQUESTED ||
            documentStatus === DocumentStatus.DOCUMENT_UNAVAILABLE
          ) {
            return {
              preview: '',
              download: null,
              isRequested: documentStatus === DocumentStatus.DOCUMENT_REQUESTED,
              isAlreadyRequested: documentStatus === DocumentStatus.DOCUMENT_ALREADY_REQUESTED,
              isUnavailable: documentStatus === DocumentStatus.DOCUMENT_UNAVAILABLE,
              extension: null,
            };
          } else {
            // Determine the file extension for UI logic
            const extension: FileExtension = this.getExtensionFromMimeType(contentType);
            // Create a Blob with the CORRECT MIME type so the browser can render it (e.g., application/pdf)
            const blob = new Blob([response.body], { type: contentType || undefined });
            const blobUrl = URL.createObjectURL(blob);
            return {
              preview: this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl),
              download: blobUrl,
              isRequested: false,
              isAlreadyRequested: false,
              isUnavailable: false,
              extension,
            };
          }
        })
      );
  }
  public getFileFromUrl(file: IResidentS3File): Observable<{
    preview: SafeResourceUrl;
    download: string;
    extension: FileExtension;
  }> {
    if (!file.url) {
      return of(null);
    }
    const extension = file.extension || getFileExtensionFromPath(file.url);

    return of({
      preview: this.sanitizer.bypassSecurityTrustResourceUrl(file.url),
      download: file.url,
      extension,
    });
  }

  public getExtensionFromUrl(url: string): FileExtension {
    return getFileExtensionFromPath(url);
  }

  public download(documentUrl: string, documentName: string) {
    const element = document.createElement('a');
    element.href = documentUrl;
    element.download = documentName;
    element.click();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public downloadDocument(_documentId: string): Observable<any> {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public downloadFile(_residentS3File: IResidentS3File): Observable<any> {
    return undefined;
  }

  public downloadTicketAttachment(
    ticketId: string,
    attachmentIdentifier: string
  ): Observable<{
    url: string;
    isRequested: boolean;
    isAlreadyRequested: boolean;
    isUnavailable: boolean;
  }> {
    const requestConfig = this.createRequestForAttachment(
      ticketId,
      attachmentIdentifier,
      this.customerIdent()
    );

    const headers = new HttpHeaders(requestConfig.headers);

    return this.http
      .post(requestConfig.url, requestConfig.content, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(response => {
          const status = response.headers.get('X-Ticket-Attachment-Status');
          const isRequested = status === TicketAttachmentStatus.TICKET_ATTACHMENT_REQUESTED;
          const isAlreadyRequested =
            status === TicketAttachmentStatus.TICKET_ATTACHMENT_ALREADY_REQUESTED;
          const isUnavailable = status === TicketAttachmentStatus.TICKET_ATTACHMENT_UNAVAILABLE;
          if (isRequested || isAlreadyRequested || isUnavailable) {
            return {
              url: null,
              isRequested,
              isAlreadyRequested,
              isUnavailable,
            };
          }
          return {
            url: URL.createObjectURL(response.body),
            isRequested: false,
            isAlreadyRequested: false,
            isUnavailable: false,
          };
        }),
        catchError(error => {
          throw new Error(error.message);
        })
      );
  }

  public getContentTitleImage(contentId: string, titleImageId: string): Observable<string> {
    const cacheKey = `${contentId}-${titleImageId}`;
    const cached = this.#titleImageCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request = this.createRequestForContentTitleImage(contentId, this.customerIdent());

    const titleImage$ = this.http
      .get(request.url, {
        headers: new HttpHeaders(request.headers),
        responseType: 'blob',
      })
      .pipe(
        map(blob => URL.createObjectURL(blob)),
        // A missing or unreadable title image must not break the surrounding page, the image slot
        // simply stays empty.
        catchError(() => of<string>(null)),
        shareReplay(1)
      );

    this.#titleImageCache.set(cacheKey, titleImage$);
    return titleImage$;
  }

  public downloadContentAttachment(
    contentId: string,
    attachmentId: string,
    extension?: string
  ): Observable<{
    preview: SafeResourceUrl;
    download: string;
    extension: FileExtension;
  }> {
    const request = this.createRequestForContentAttachment(
      contentId,
      attachmentId,
      this.customerIdent()
    );

    return this.http
      .get(request.url, {
        headers: new HttpHeaders(request.headers),
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(response => {
          const contentType = response.headers.get('content-type');
          const blob = new Blob([response.body], { type: contentType || undefined });
          const blobUrl = URL.createObjectURL(blob);

          return {
            preview: this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl),
            download: blobUrl,
            extension: (extension as FileExtension) ?? this.getExtensionFromMimeType(contentType),
          };
        }),
        catchError((error: HttpErrorResponse) => {
          // Keep the status on the message, the bare HttpErrorResponse message omits it.
          throw new Error(
            `Content attachment download failed with status ${error.status}: ${error.message}`
          );
        })
      );
  }
}
